/**
 * Смерть: проверка по годовой вероятности и розыгрыш причины.
 *
 * Причина — из долей ВОЗ (регион × возрастная полоса, `CAUSE_SHARES`)
 * с личными поправками: `neonatal` только в первый год жизни (в полосе 0–4
 * его доля при age > 0 обнуляется, остаток нормируется), суицид только
 * с 13 лет, `maternal` только у женщин,
 * условие `hiv` утраивает вес инфекций, `chronic` — ×1.5 для сердца
 * и диабета. Тяжёлые причины (`HEAVY_CAUSES`) никогда не описывают способ:
 * при `flags.skipHeavy` запись заменяется нейтральной `death.neutral`,
 * статистика не меняется; за тяжёлой причиной всегда идёт `help.lines`.
 *
 * Все случайности — из переданного `Rng`; функции не мутируют вход.
 */
import {
  CAUSE_SHARES,
  HEAVY_CAUSES,
  getCountry,
  type AgeBand,
  type CauseId,
  type Dataset,
  type WhoRegion,
} from '@life/data';
import { CONDITION_CHRONIC, CONDITION_HIV, yearlyMortality } from './health';
import type { Rng } from './rng';
import type { Life, LogEntry } from './types';

/** Минимальный возраст, с которого суицид может быть причиной. */
export const SUICIDE_MIN_AGE = 13;
/** Неонатальная смерть возможна только в первый год жизни. */
export const NEONATAL_MAX_AGE = 0;
const HIV_INFECTION_WEIGHT = 3;
const CHRONIC_WEIGHT = 1.5;

export const DEATH_NEUTRAL_KEY = 'death.neutral';
export const HELP_KEY = 'help.lines';

/** Возрастная полоса ВОЗ. */
export function ageBand(age: number): AgeBand {
  if (age < 5) return '0-4';
  if (age < 15) return '5-14';
  if (age < 30) return '15-29';
  if (age < 50) return '30-49';
  if (age < 70) return '50-69';
  return '70+';
}

/** Веса причин для этого человека: доли полосы × личные поправки. */
function causeWeights(region: WhoRegion, life: Life): { causes: CauseId[]; weights: number[] } {
  const shares = CAUSE_SHARES[region][ageBand(life.age)];
  const hiv = life.conditions.includes(CONDITION_HIV);
  const chronic = life.conditions.includes(CONDITION_CHRONIC);
  const causes: CauseId[] = [];
  const weights: number[] = [];
  for (const [cause, share] of Object.entries(shares) as [CauseId, number][]) {
    let w = share;
    if (cause === 'neonatal' && life.age > NEONATAL_MAX_AGE) w = 0;
    if (cause === 'suicide' && life.age < SUICIDE_MIN_AGE) w = 0;
    if (cause === 'maternal' && life.sex !== 'f') w = 0;
    if (cause === 'infection' && hiv) w *= HIV_INFECTION_WEIGHT;
    if ((cause === 'cardio' || cause === 'diabetes') && chronic) w *= CHRONIC_WEIGHT;
    if (w > 0) {
      causes.push(cause);
      weights.push(w);
    }
  }
  return { causes, weights };
}

/** Розыгрыш причины смерти по региону, возрасту, полу и условиям. */
export function drawCause(region: WhoRegion, life: Life, rng: Rng): CauseId {
  const { causes, weights } = causeWeights(region, life);
  return rng.weighted(causes, weights);
}

/**
 * Проверка смерти в этом году. При смерти: alive=false, death={age,cause,heavy};
 * запись `death.<cause>` (или `death.neutral` при heavy и flags.skipHeavy),
 * для тяжёлой причины — ещё `help.lines`. Мёртвую жизнь возвращает как есть.
 */
export function checkDeath(ds: Dataset, life: Life, rng: Rng): { life: Life; entries: LogEntry[] } {
  if (!life.alive) return { life, entries: [] };
  const q = yearlyMortality(ds, life);
  if (!rng.chance(q)) return { life, entries: [] };

  const region = getCountry(ds, life.iso3).whoRegion;
  const cause = drawCause(region, life, rng);
  const heavy = HEAVY_CAUSES.has(cause);
  const skip = heavy && Boolean(life.flags['skipHeavy']);
  const entries: LogEntry[] = [
    { age: life.age, key: skip ? DEATH_NEUTRAL_KEY : `death.${cause}`, params: { age: life.age }, heavy },
  ];
  if (heavy) entries.push({ age: life.age, key: HELP_KEY, heavy: true });

  return {
    life: { ...life, alive: false, death: { age: life.age, cause, heavy } },
    entries,
  };
}
